import { Inject, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import i18next from 'i18next';
import { CollectionsService } from '../../modules/collections/service/collections.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { NativeIntegrationService } from './native-integration.service';
import { BridgeMode } from '@hg-com/xwc-js-bridge/dist/native-app.service';
import { LanguageService } from './language.service';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from 'src/core/services/analytics.service';
import { AuthService } from 'src/core/services/auth.service';

export function extractErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error';

  if (error instanceof ProgressEvent) {
    const xhr = error.target as XMLHttpRequest | null;
    const eventType = error.type || 'error';
    if (xhr?.status) {
      return `Network ${eventType}: ${xhr.status} ${
        xhr.statusText || ''
      }`.trim();
    }
    return `Network ${eventType} - please check your connection`;
  }

  if (typeof error === 'object') {
    const e = error as any;
    if (e.message) return e.message;
    if (e.statusText) return `${e.status}: ${e.statusText}`;
    if (e.text) return e.text;
    if (e.error?.message) return e.error.message;
    if (e.error instanceof ProgressEvent) return extractErrorMessage(e.error);
  }

  if (typeof error === 'string') return error;

  return 'An unexpected error occurred';
}

@Injectable()
export class DataService {
  dataPriceLists: any;
  filterList: any;
  context: any;
  priceLists: string[] = [];
  activePriceList = localStorage.getItem('priceListActive');
  blappPrices!: string;
  availableLangs: any;
  blappArticles!: string;
  legalFileName!: string;
  legals$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  contextLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  translationsLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  dataLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  route = '/api/v1';
  urlParamContext = '/xml/latest';
  urlParamFile = '/file';
  apiUrl = environment.apiBaseUrl + this.route;
  pdfUrl = environment.apiBaseUrl + this.route;
  xlsxUrl = environment.apiBaseUrl + this.route;
  lang = 'de';

  constructor(
    private http: HttpClient,
    public collectionsService: CollectionsService,
    protected nativeIntegrationService: NativeIntegrationService,
    private router: Router,
    @Inject(Injector) private injector: Injector,
    private languageService: LanguageService
  ) {
    this.initRoute().then();
    this.languageService.i18NextService.events.initialized.subscribe((e) => {
      if (e) {
        console.log('[DATA SERVICE]: Language subscription');
        this.updateState(this.languageService.i18NextService.language);
      }
    });
  }

  // Need to get ToastrService from injector rather than constructor injection to avoid cyclic dependency error
  private get toastrService(): ToastrService {
    return this.injector.get(ToastrService);
  }

  private handlePackLoadError(error: any): void {
    console.log('[DATA SERVICE]: Pack load error');
    console.log(error);
    this.loading$.next(false);
    this.toastrService.error(extractErrorMessage(error), 'ERROR', {
      closeButton: true,
      timeOut: 5000,
      onActivateTick: true,
    });
    if (this.nativeIntegrationService.isNative$.getValue()) {
      Promise.allSettled([
        this.nativeIntegrationService.contentPack.deletePack$('latest', 'data'),
        this.nativeIntegrationService.contentPack.deletePack$(
          'latest',
          'media'
        ),
      ]).then(() => this.logout());
    } else {
      this.logout();
    }
  }

  load(): any {}

  private updateState(lang: string): void {
    console.log('[DATA SERVICE]: Language set');
    this.lang = lang;
  }

  public initRoute(): Promise<any> {
    return this.nativeIntegrationService.native
      .sleepToInitWithTimeout$(1000, false)
      .then(() => {
        if (this.nativeIntegrationService.native.mode > BridgeMode.Mock) {
          console.log('[DATA SERVICE]: init Routes native');
          this.urlParamContext = '/xml/latest';
          this.urlParamFile = '/file';
          this.apiUrl =
            `http://localhost:${this.nativeIntegrationService.native.port}/xwc/atum` +
            this.route;
          const nativeBaseUrl = `http://localhost:${this.nativeIntegrationService.native.port}/xwc`;
          this.pdfUrl = nativeBaseUrl;
          this.xlsxUrl = nativeBaseUrl;
          this.nativeIntegrationService
            .readyForOfflineMode()
            .then((offlineReady) => {
              if (offlineReady) {
                console.log(
                  '[DATA SERVICE]: Init Routes native - changed Api Url to offline route'
                );
                this.urlParamFile = '';
                this.urlParamContext = '';
                this.apiUrl = `http://localhost:${this.nativeIntegrationService.native.port}/xwc/pack/latest/data`;
              }
            });
        } else {
          console.log('[DATA SERVICE]: Init Routes online');
          this.apiUrl = environment.apiBaseUrl + this.route;
          this.pdfUrl = this.apiUrl;
          this.xlsxUrl = this.apiUrl;
        }
        console.log(this.apiUrl);
      });
  }

  getContext(): void {
    console.log('[DATA SERVICE]: Get context');
    this.loading$.next(true);
    this.http
      .get(
        this.apiUrl + this.urlParamContext + '/context.json?_nocache=12345',
        { observe: 'response' }
      )
      .subscribe(
        (resFull) => {
          const res = resFull.body;
          console.log('[DATA SERVICE]: Context loaded success');
          this.context = res;
          this.priceLists = this.context.priceLists.sort((a: string, b: any) =>
            a.localeCompare(b)
          );
          console.log(this.priceLists);
          this.availableLangs = this.context.languages;
          this.blappPrices = this.context.pathMap.BPLAPP_Prices;
          console.log(this.blappPrices);
          this.blappArticles = this.context.pathMap.BPLAPP_Articles;
          this.legalFileName = this.context.pathMap.legal;
          this.loading$.next(false);
          this.contextLoaded$.next(true);
          this.getData();
          const analyticsService =
            this.injector.get<AnalyticsService>(AnalyticsService);
          const authService = this.injector.get<AuthService>(AuthService);
          if (authService.didLogin) {
            authService.didLogin = false;
            analyticsService.trackEventWithCustomerGroup({
              event: 'account',
              // @ts-ignore
              category: 'bpl app',
              step: 'login',
            });
          }
        },
        (error) => this.handlePackLoadError(error)
      );
  }

  getData(): void {
    console.log('[DATA SERVICE]: Get data with context');

    if (localStorage.getItem('priceList') === null) {
      console.log('[DATA SERVICE]: No price list set');

      this.activePriceList = localStorage.getItem('priceListActive');
      if (this.activePriceList === null) {
        this.activePriceList = this.priceLists[0];
      }
      this.loading$.next(true);

      this.http
        .get(
          this.apiUrl +
            this.urlParamContext +
            this.urlParamFile +
            '/' +
            this.blappPrices +
            '.' +
            this.activePriceList +
            '.json'
        )
        .subscribe(
          (data) => {
            console.log('[DATA SERVICE]: price list data loaded success');
            this.dataPriceLists = data;
            console.log(this.dataPriceLists);
            this.getLegal(this.lang);
            this.getTranslations(this.lang);
            this.setData();
            this.dataLoaded$.next(true);
          },
          (error) => this.handlePackLoadError(error)
        );
    } else {
      console.log('[DATA SERVICE]: Price list set in local storage');
      this.dataPriceLists = JSON.parse(
        '' + localStorage.getItem('priceList') + ''
      );
      this.getLegal(this.lang);
      this.getTranslations(this.lang);
      this.setData();
      this.dataLoaded$.next(true);
    }

    this.http
      .get(
        this.apiUrl +
          this.urlParamContext +
          this.urlParamFile +
          '/' +
          this.context.pathMap.Filters +
          '.json'
      )
      .subscribe(
        (data) => {
          this.filterList = JSON.parse(JSON.stringify(data));
          console.log('[DATA SERVICE]: Filter Data loaded success');
        },
        (error) => this.handlePackLoadError(error)
      );
  }

  setData(): void {
    console.log('[DATA SERVICE]: Set data in services');
    if (this.activePriceList === null) {
      this.activePriceList = this.priceLists[0];
    }
    localStorage.setItem('priceListActive', this.activePriceList);
    this.collectionsService.articlesData = this.dataPriceLists.articles;
    this.collectionsService._collectionsData = this.dataPriceLists.collections;
    this.collectionsService.currencyCode = this.dataPriceLists.currency;
    this.collectionsService.emitLoadDataSuccess();
    this.collectionsService.dataLoaded = true;
  }

  getTranslations(lang: string): void {
    console.log('[DATA SERVICE]: Get Translations');
    this.translationsLoaded$.next(false);
    if (lang) {
      this.http
        .get(
          this.apiUrl +
            this.urlParamContext +
            this.urlParamFile +
            '/' +
            this.blappArticles +
            '.i18n.' +
            lang +
            '.json'
        )
        .subscribe((data) => {
          console.log('[DATA SERVICE]: Translations Articles loaded success');
          this.collectionsService.translations = JSON.parse(
            JSON.stringify(data)
          ).text;
          i18next.addResources(
            lang,
            'translations',
            JSON.parse(JSON.stringify(data)).text
          );
          this.translationsLoaded$.next(true);
          console.log(i18next.hasResourceBundle(lang, 'translations'));
          this.loading$.next(false);
        });
      this.http
        .get(
          this.apiUrl +
            this.urlParamContext +
            this.urlParamFile +
            '/' +
            this.blappPrices +
            '.i18n.' +
            lang +
            '.json'
        )
        .subscribe((data) => {
          console.log('[DATA SERVICE]: Translations Prices loaded success');
          i18next.addResources(
            lang,
            'translations',
            JSON.parse(JSON.stringify(data)).text
          );
          console.log(data);
          this.loading$.next(false);
        });
    }
  }

  getLegal(lang: string): void {
    console.log('[DATA SERVICE]: Get legal');
    combineLatest([
      this.http.get(
        this.apiUrl +
          this.urlParamContext +
          this.urlParamFile +
          '/' +
          this.legalFileName +
          '.json'
      ),
      this.http.get(
        this.apiUrl +
          this.urlParamContext +
          this.urlParamFile +
          '/' +
          this.legalFileName +
          '.i18n.' +
          lang +
          '.json'
      ),
    ]).subscribe(([legalData, legalTranslations]) => {
      if (!legalData || !legalTranslations) {
        console.log('race', legalData, legalTranslations);
      }

      console.log('[DATA SERVICE]: Legal Translation loaded success');

      i18next.addResources(
        lang,
        'translations',
        JSON.parse(JSON.stringify(legalTranslations)).text
      );
      console.log(i18next.hasResourceBundle(lang, 'translations'));

      console.log('[DATA SERVICE]: Legal Data loaded success');
      console.log(legalData);
      this.legals$.next(legalData);
      this.loading$.next(false);
    });
  }

  generatePDF(
    cart: any,
    vendorName: string,
    lang: string,
    address: string[],
    translations: string[]
  ): void {
    console.log('[DATA SERVICE]: Generate PDF');

    this.http
      .post(
        this.pdfUrl + '/print/digital-bag',
        {
          pdfOutputMode: 1,
          lang,
          vendor_name: vendorName,
          cart,
          address,
          translations,
        },
        {
          responseType: 'blob',
        }
      )
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          console.log('[DATA SERVICE]: Generate PDF success');
          console.log(response);
          const downloadLink = document.createElement('a');
          const file = new Blob([response], { type: 'application/pdf' });
          downloadLink.href = URL.createObjectURL(file);
          const fileName = 'VB_' + this.getDate();
          downloadLink.download = fileName;
          downloadLink.click();
        },
        error: (error: any) => {
          console.error(error);
          if (error?.status === 503) {
            this.toastrService.error(i18next.t('cart.pdf-offline'));
            return;
          }
          this.toastrService.error(extractErrorMessage(error), 'ERROR', {
            closeButton: true,
            timeOut: 5000,
            onActivateTick: true,
          });
        },
      });
  }

  generateXslx(
    cart: any,
    vendorName: string,
    lang: string,
    address: string[],
    translations: string[]
  ): void {
    console.log('[DATA SERVICE]: Generate xlsx');

    this.http
      .post(
        this.xlsxUrl + '/print/digital-bag-xlsx',
        {
          lang,
          vendor_name: vendorName,
          cart,
          address,
          translations,
        },
        {
          headers: {
            accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          responseType: 'blob',
        }
      )
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          console.log('[DATA SERVICE]: Generate xlsx success');
          console.log(response);
          const downloadLink = document.createElement('a');
          const file = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          downloadLink.href = URL.createObjectURL(file);
          const fileName = 'VB_' + this.getDate() + '.xlsx';
          downloadLink.download = fileName;
          downloadLink.click();
        },
        error: (error: any) => {
          if (error?.status === 503) {
            this.toastrService.error(i18next.t('cart.xlsx-offline'));
            return;
          }
          this.toastrService.error(extractErrorMessage(error), 'ERROR', {
            closeButton: true,
            timeOut: 5000,
            onActivateTick: true,
          });
        },
      });
  }

  getDate(): string {
    const date = new Date();
    return date.getFullYear() + '-' + date.getMonth() + '-' + date.getDate();
  }

  changePriceList(priceList: string): void {
    console.log('[DATA SERVICE]: Change price list');

    this.dataLoaded$.next(false);
    this.collectionsService.dataLoaded = false;
    this.http
      .get(
        this.apiUrl +
          this.urlParamContext +
          this.urlParamFile +
          '/' +
          this.blappPrices +
          '.' +
          priceList +
          '.json'
      )
      .subscribe((data) => {
        console.log('[DATA SERVICE]: Change price list success');
        this.dataPriceLists = data;
        this.collectionsService.articlesData = this.dataPriceLists.articles;
        this.collectionsService._collectionsData =
          this.dataPriceLists.collections;
        this.collectionsService.currencyCode = this.dataPriceLists.currency;
        this.collectionsService.emitLoadDataSuccess();
        this.collectionsService.dataLoaded = true;
        localStorage.setItem('priceList', JSON.stringify(this.dataPriceLists));
        localStorage.setItem('priceListActive', priceList);
        this.dataLoaded$.next(true);
      });
  }

  async logout(): Promise<void> {
    const authService = this.injector.get<AuthService>(AuthService);
    const loginStatus = authService.isAuthenticated$.getValue();
    console.log('[DATA SERVICE]: Logout');
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('priceListActive');
    localStorage.removeItem('priceLists');
    localStorage.removeItem('cartState');
    localStorage.removeItem('deviceLangSet');
    localStorage.removeItem('communityToken');
    localStorage.removeItem('sftoken');
    localStorage.removeItem('sftokencreated');
    this.translationsLoaded$.next(false);
    this.dataLoaded$.next(false);
    this.loading$.next(false);
    await authService.saveAuthenticated(false);
    authService.isAuthenticatedOffline$.next(false);
    await authService.saveUsername(null);
    await authService.clearPin();
    authService.error.next(false);

    if (this.nativeIntegrationService.isNative$.getValue()) {
      this.nativeIntegrationService.offlineMode$.next(false);
      const promises = [
        this.nativeIntegrationService.storage.secureStoreDelete$('pin'),
        this.nativeIntegrationService.storage.secureStoreDelete$('username'),
        this.nativeIntegrationService.storage.secureStoreDelete$('password'),
        this.nativeIntegrationService.storage.secureStoreDelete$(
          'communityToken'
        ),
        this.nativeIntegrationService.storage.secureStoreDelete$('sftoken'),
        this.nativeIntegrationService.storage.secureStoreDelete$(
          'sftokencreated'
        ),
        authService.saveAuthenticated(false),
        this.nativeIntegrationService.contentPack.deletePack$('latest', 'data'),
        this.nativeIntegrationService.clientAuth.logout$(),
      ];
      Promise.allSettled(promises)
        .then(() => this.initRoute())
        .then(async () => {
          if (loginStatus) {
            // Only reload if we where logged in
            // else we get into an infinite loop where authstate checks if we are logged in on startup
            // and logs us out if we are not logged in
            await this.router.navigate(['/']);
            window.location.reload();
          }
        });
    } else {
      this.router.navigate(['/']).then(() => {
        if (loginStatus) {
          window.location.reload();
        }
      });
    }
  }
}

export function initializeApp(dataService: DataService): any {
  return () => dataService.load();
}
